/**
 * Keeps the horizontal scrollbar of every wide table within reach.
 *
 * A table inside an `overflow-x-auto` box has its scrollbar at the bottom of the box — which, for a long table, is far
 * below the fold, so you had to scroll to the end of the page just to scroll sideways. This watches the page and, for
 * each wide table whose own scrollbar is off-screen, floats a synced copy of the scrollbar along the bottom edge of the
 * visible area (over the table's columns). When the table's real scrollbar comes into view the copy disappears.
 *
 * Nothing needs to be added to individual pages: any tall-enough box that scrolls sideways (wide tables, Kanban boards)
 * is picked up automatically, including ones inside drawers and dialogs. Short strips such as tabs are left alone.
 */

const SELECTOR = ".overflow-x-auto, .overflow-x-scroll, .overflow-auto, .overflow-scroll, [style*=\"overflow\"]";
const MIN_HEIGHT = 120; // tab strips, chip rows and the like are short and keep their own scrollbar in reach
const BAR_HEIGHT = 14;

interface Item {
  el: HTMLElement;
  bar: HTMLDivElement;
  inner: HTMLDivElement;
  onEl: () => void;
  onBar: () => void;
}

/** The nearest ancestor that scrolls vertically (the page's <main> in this app), else the window. */
function scrollParent(el: HTMLElement): HTMLElement | null {
  for (let p = el.parentElement; p; p = p.parentElement) {
    const o = getComputedStyle(p).overflowY;
    if ((o === "auto" || o === "scroll") && p.scrollHeight > p.clientHeight + 1) return p;
  }
  return null;
}

function eligible(el: HTMLElement): boolean {
  if (!el.isConnected || el.classList.contains("sticky-xbar")) return false;
  if (el.scrollWidth <= el.clientWidth + 2) return false; // not actually wide
  if (el.offsetHeight < MIN_HEIGHT) return false;
  const ox = getComputedStyle(el).overflowX;
  return ox === "auto" || ox === "scroll";
}

export function installStickyScrollbars(): () => void {
  if (typeof window === "undefined") return () => undefined;
  const items = new Map<HTMLElement, Item>();
  let raf = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;

  function make(el: HTMLElement): Item {
    const bar = document.createElement("div");
    bar.className = "sticky-xbar";
    bar.setAttribute("aria-hidden", "true");
    const inner = document.createElement("div");
    inner.style.height = "1px";
    bar.appendChild(inner);
    Object.assign(bar.style, {
      position: "fixed",
      zIndex: "35",
      height: `${BAR_HEIGHT}px`,
      overflowX: "auto",
      overflowY: "hidden",
      display: "none",
      background: "rgba(248,250,252,0.97)",
      borderTop: "1px solid #e2e8f0",
      borderRadius: "8px 8px 0 0",
      boxShadow: "0 -3px 8px rgba(15,23,42,0.08)",
    } as Partial<CSSStyleDeclaration>);
    document.body.appendChild(bar);
    // Each side copies the other only when they differ, so the two never ping-pong.
    const item: Item = {
      el,
      bar,
      inner,
      onEl: () => {
        if (bar.scrollLeft !== el.scrollLeft) bar.scrollLeft = el.scrollLeft;
      },
      onBar: () => {
        if (el.scrollLeft !== bar.scrollLeft) el.scrollLeft = bar.scrollLeft;
      },
    };
    el.addEventListener("scroll", item.onEl, { passive: true });
    bar.addEventListener("scroll", item.onBar, { passive: true });
    return item;
  }

  function drop(el: HTMLElement) {
    const it = items.get(el);
    if (!it) return;
    el.removeEventListener("scroll", it.onEl);
    it.bar.remove();
    items.delete(el);
  }

  function position(it: Item) {
    const { el, bar, inner } = it;
    const rect = el.getBoundingClientRect();
    const sp = scrollParent(el);
    const spRect = sp?.getBoundingClientRect();
    let boundary = Math.min(window.innerHeight, spRect?.bottom ?? window.innerHeight); // bottom of what is visible
    const bottomNav = document.querySelector<HTMLElement>("nav.fixed.bottom-0"); // phone tab bar sits over the page
    if (bottomNav && bottomNav.offsetHeight > 0) boundary = Math.min(boundary, bottomNav.getBoundingClientRect().top);
    const left = Math.max(rect.left, spRect?.left ?? 0, 0);
    const right = Math.min(rect.right, spRect?.right ?? window.innerWidth, window.innerWidth);
    // Show only while part of the table is on screen but its own scrollbar (its bottom edge) is below the fold.
    const show = right - left > 40 && rect.top < boundary - BAR_HEIGHT - 20 && rect.bottom > boundary + 2;
    if (!show) {
      bar.style.display = "none";
      return;
    }
    bar.style.display = "block";
    bar.style.left = `${left}px`;
    bar.style.width = `${right - left}px`;
    bar.style.bottom = `${window.innerHeight - boundary}px`;
    inner.style.width = `${el.scrollWidth}px`;
    if (bar.scrollLeft !== el.scrollLeft) bar.scrollLeft = el.scrollLeft;
  }

  function scan() {
    raf = 0;
    clearTimeout(timer);
    timer = undefined;
    const seen = new Set<HTMLElement>();
    document.querySelectorAll<HTMLElement>(SELECTOR).forEach((el) => {
      if (!eligible(el)) return;
      seen.add(el);
      if (!items.has(el)) items.set(el, make(el));
    });
    for (const el of [...items.keys()]) if (!seen.has(el)) drop(el);
    items.forEach(position);
  }
  const schedule = () => {
    if (raf || timer) return;
    raf = requestAnimationFrame(scan);
    timer = setTimeout(() => {
      cancelAnimationFrame(raf); // animation frames are paused in background tabs — fall back to a timer
      scan();
    }, 120);
  };

  const mo = new MutationObserver(schedule);
  mo.observe(document.body, { childList: true, subtree: true });
  const ro = new ResizeObserver(schedule);
  ro.observe(document.body);
  window.addEventListener("resize", schedule);
  document.addEventListener("scroll", schedule, true); // any scroller, including <main>
  schedule();

  return () => {
    mo.disconnect();
    ro.disconnect();
    window.removeEventListener("resize", schedule);
    document.removeEventListener("scroll", schedule, true);
    if (raf) cancelAnimationFrame(raf);
    clearTimeout(timer);
    for (const el of [...items.keys()]) drop(el);
  };
}
